import React from 'react'
import { useNavigate } from 'react-router-dom'

import { Wizard } from '@factorialco/deprecated-design-system'
import type {
  AttendanceEnumsEmployeeTypeEnum,
  ExtraHoursRangeFragment,
  SettingPageEmployeesSettingFragment,
} from 'generated/resources/graphql'
import { isEqual, omit } from 'lodash'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import ManageSettingVersionModal from 'modules/attendance/components/SettingItemPage/TimeTracking/ManageSettingVersionModal'
import { convertBackendMinutesToHours } from 'modules/attendance/components/SettingItemPage/TimeTracking/WorkTimeCategories/Content/utils/extraHoursUtils'
import { useAttendanceSettingWorkCategories } from 'modules/attendance/lib/attendanceSettingWorkCategoriesContext'
import { EXTRA_HOURS_RANGE_BASELINE } from 'modules/attendance/lib/enums'
import { ExtraHoursRulesData } from 'modules/attendance/lib/forms/utils/ExtraHourRulesWizardData'
import { centsToMinutes } from 'modules/attendance/lib/timeConverter'
import { EMPLOYEE_TYPE } from 'modules/attendance/lib/types'
import { MODAL_LOCK_TIMEOUT } from 'modules/attendance/lib/utils'
import ExtraHoursRangeUtils from 'modules/attendance/lib/utils/ExtraHoursRangeUtils'

import { basicDetailsStep } from './BasicDetails'
import { compensationMethodStep } from './CompensationMethod'
import { hourlyRateStep } from './HourlyRate'
import { useExtraHourRules } from './useExtraHourRules'

interface Props {
  extraHourRule?: ExtraHoursRangeFragment
  fromHourRange?: number
  employeeType?: AttendanceEnumsEmployeeTypeEnum // Explicit employeeType for new rules
  onClose: () => void
}

const HoursRulesWizard = ({ extraHourRule, fromHourRange = 1, employeeType, onClose }: Props) => {
  const [isLocked, setIsLocked] = React.useState(true)
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const {
    attendanceSetting,
    companyId,
    contractTaxonomies,
    payrollConcepts,
    payrollEnabled,
    payrollMappingsTaxonomies,
    payrollMappingsCompensations,
    hasCompensations,
    hasContractHoursBaseline,
  } = useAttendanceSettingWorkCategories()

  const isEdit = Boolean(extraHourRule)

  // When opening the Wizard from the PopoverMenu, clicking on "Edit", the click event passes through
  // to the backdrop (overlay) and immediately closes the Wizard.
  // We temporarily lock the modal for ${MODAL_LOCK_TIMEOUT}ms to wait for the PopoverMenu
  // animation to complete, preventing click-outside interference.
  // Note: we can't use the built-in "modal" Wizard prop as its focus trap breaks form components.
  useEffect(() => {
    setTimeout(() => setIsLocked(false), MODAL_LOCK_TIMEOUT)
  }, [])

  const { fromHour, toHour } = extraHourRule
    ? convertBackendMinutesToHours(extraHourRule.fromMinute, extraHourRule.toMinute)
    : { fromHour: fromHourRange, toHour: undefined }

  const baselineFromRule: EXTRA_HOURS_RANGE_BASELINE | undefined = extraHourRule?.baseline
    ? extraHourRule.baseline === EXTRA_HOURS_RANGE_BASELINE.contract_hours
      ? EXTRA_HOURS_RANGE_BASELINE.contract_hours
      : EXTRA_HOURS_RANGE_BASELINE.effective_hours
    : undefined

  const computedInitialBaseline: EXTRA_HOURS_RANGE_BASELINE =
    baselineFromRule ??
    (hasContractHoursBaseline
      ? EXTRA_HOURS_RANGE_BASELINE.contract_hours
      : EXTRA_HOURS_RANGE_BASELINE.effective_hours)

  const resolvedEmployeeType: AttendanceEnumsEmployeeTypeEnum =
    employeeType ?? extraHourRule?.employeeType ?? EMPLOYEE_TYPE.FULL_TIME
  const isComplementaryHours = resolvedEmployeeType === EMPLOYEE_TYPE.PART_TIME

  const initialData: ExtraHoursRulesData = {
    id: extraHourRule?.id.toString(),
    poolType: extraHourRule?.poolType ?? 'timeoff',
    rawMinutes: extraHourRule?.rawMinutesInCents
      ? centsToMinutes(extraHourRule?.rawMinutesInCents)
      : 60,
    equivalentMinutes: extraHourRule?.equivalentMinutesInCents
      ? centsToMinutes(extraHourRule?.equivalentMinutesInCents)
      : 60,
    fromHour,
    toHour,
    baseline: computedInitialBaseline,
    originalBaseline: baselineFromRule,
    errors: {},
    rangeType: !!extraHourRule?.unlimited ? 'unlimited' : 'fixed',
    canCreateSettingCategory: true,
    attendanceEmployeesSettingId: attendanceSetting.id.toString(),
    timeSettingsCustomTimeRangeCategoryId: extraHourRule?.timeSettingsCustomTimeRangeCategory?.id,
    payrollEnabled,
    contractTaxonomies,
    payrollConcepts,
    payrollMappingsTaxonomies,
    payrollMappingsCompensations,
    formMappingsTaxonomies: [],
    hasCompensations,
    isEdit,
    isDefault: extraHourRule ? ExtraHoursRangeUtils.isDefault(extraHourRule) : false,
    isUnlimited: function () {
      return this.rangeType === 'unlimited'
    },
    employeeType: resolvedEmployeeType,
  }

  const [wizardData, setWizardData] = React.useState<ExtraHoursRulesData>(initialData)
  const [newSettingData, setNewSettingData] = React.useState<ExtraHoursRulesData | null>(null)
  const [openSettingsVersionModal, setOpenSettingsVersionModal] = React.useState(false)

  const { onSubmit } = useExtraHourRules({
    settingId: attendanceSetting.id,
    attendanceSetting,
    hasContractHoursBaseline,
    companyId,
  })

  const onCreateSetting = async (newSetting: SettingPageEmployeesSettingFragment) => {
    const { extraHoursRange } = newSetting ?? {}
    const extraHourRuleCopy = omit(extraHourRule, ['id', 'attendanceEmployeesSettingId'])

    const matchingRange = extraHoursRange.find((e) =>
      isEqual(extraHourRuleCopy, omit(e, ['id', 'attendanceEmployeesSettingId']))
    )

    const data = {
      ...wizardData,
      ...(matchingRange && {
        id: matchingRange.id.toString(),
        attendanceEmployeesSettingId: matchingRange.attendanceEmployeesSettingId.toString(),
      }),
    }

    setNewSettingData(data)
  }

  const closeModalAndSubmit = async (data: ExtraHoursRulesData) => {
    setOpenSettingsVersionModal(false)
    const success = await onSubmit(data)

    if (success) {
      onClose()
    }
  }

  const onSubmitWizard = (data: ExtraHoursRulesData) => {
    wizardData.isDefault
      ? track(trackingEvents.timeTracking.baseRuleSubmitPressed)
      : track(trackingEvents.timeTracking.customRuleSubmitPressed, { isEdit })

    if (!!extraHourRule) {
      setWizardData(data)
      return setOpenSettingsVersionModal(true)
    }

    closeModalAndSubmit(data)
  }

  useEffect(() => {
    if (newSettingData) {
      closeModalAndSubmit(newSettingData)

      const routeParams = {
        id: 'time_tracking',
        settingId: newSettingData?.attendanceEmployeesSettingId,
        tabId: 'work_time_categories',
      }

      navigate(navigation.settings.time_tracking.settingId.tabId.fullPath(routeParams))
    }
  }, [newSettingData?.id])
  const title = isEdit
    ? isComplementaryHours
      ? i18n.t(
          'time_tracking.settings.work_time_categories.complementary_hours_rules.new_rule_wizard.edit_title'
        )
      : i18n.t(
          'time_tracking.settings.work_time_categories.extra_hours_rules.new_rule_wizard.edit_title'
        )
    : isComplementaryHours
    ? i18n.t(
        'time_tracking.settings.work_time_categories.complementary_hours_rules.new_rule_wizard.title'
      )
    : i18n.t('time_tracking.settings.work_time_categories.extra_hours_rules.new_rule_wizard.title')

  const icon = isEdit ? 'document-edit' : 'holidays-event-tenure'

  return (
    <>
      {!openSettingsVersionModal && (
        <Wizard.Modal
          size="regular"
          onClose={onClose}
          title={title}
          icon={icon}
          steps={
            wizardData.isDefault
              ? [compensationMethodStep()]
              : [basicDetailsStep(), hourlyRateStep(), compensationMethodStep()]
          }
          initialData={wizardData}
          onSubmit={onSubmitWizard}
          locked={isLocked}
        />
      )}
      {openSettingsVersionModal && (
        <ManageSettingVersionModal
          onCreate={onCreateSetting}
          onUpdate={() => closeModalAndSubmit(wizardData)}
          onClose={() => setOpenSettingsVersionModal(false)}
          running={false}
          attendanceSettingId={attendanceSetting.id}
          attendanceSettingEffectiveAt={attendanceSetting.attributes.effective_at}
        />
      )}
    </>
  )
}

export default HoursRulesWizard
